import React, { useState, useEffect, useRef } from 'react';
import { X, Loader2, PauseCircle, CalendarDays, TriangleAlert, Users } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { Badge } from '../../../../components/ui/Badge';


export const ExecutionWOInfoCard = ({ workOrder }) => {

    const [isOverdue, setIsOverdue] = useState(false);
    if (!workOrder) {
        return (
            <div className="rounded-2xl border border-gray-700 bg-gray-800/50 text-gray-500 shadow-lg p-6 flex items-center justify-center text-sm italic">
                No work order selected
            </div>
        );
    }

    const [wo_progress, setWoProgress] = useState(0);

    useEffect(() => {
        setWoProgress(workOrder && workOrder.tasks && workOrder.tasks.length > 0 ? Math.floor(workOrder.tasks.reduce((acc, task) => task.status === 'completed' ? acc + 1 : acc, 0) * 100 / workOrder.tasks.length) : 0);
    }, [workOrder.tasks]);


    // Status colors — subdued tints so they read clearly without blaring
    const statusColorMap = {
        scheduled: "bg-blue-500/15 text-blue-300 border border-blue-500/25",
        in_progress: "bg-amber-500/15 text-amber-300 border border-amber-500/25",
        completed: "bg-emerald-500/15 text-emerald-300 border border-emerald-500/25",
        cancelled: "bg-red-500/15 text-red-400 border border-red-500/25",
    };

    // Priority colors — still distinct but not full-saturation solids
    const priorityColorMap = {
        low: { badge: "bg-cyan-500/20 text-cyan-300", bar: "bg-cyan-500/50", stripe: "bg-cyan-400", stripeW: "w-[3px]" },
        medium: { badge: "bg-amber-500/20 text-amber-300", bar: "bg-amber-500/50", stripe: "bg-amber-400", stripeW: "w-[3px]" },
        high: { badge: "bg-red-500/20 text-red-300", bar: "bg-red-500/50", stripe: "bg-red-500", stripeW: "w-[3px]" },
        critical: { badge: "bg-rose-800/70 text-rose-200", bar: "bg-rose-600/70", stripe: "bg-rose-500", stripeW: "w-[5px]" },
    };

    const priority = workOrder.priority || 'low';
    const pColors = priorityColorMap[priority] || priorityColorMap.low;
    const isCritical = priority === 'critical';

    const getDaysDifference = (scheduledDateStr) => {
        if (!scheduledDateStr) return null;
        const scheduled = new Date(scheduledDateStr);
        const now = new Date();

        // Reset times to compare dates only
        scheduled.setHours(0, 0, 0, 0);
        now.setHours(0, 0, 0, 0);

        const diffTime = scheduled - now;
        return Math.round(diffTime / (1000 * 60 * 60 * 24));
    };

    const diffDays = getDaysDifference(workOrder.scheduled_date);
    let diffDaysText = "";
    if (diffDays !== null) {
        if (diffDays === 0) {
            diffDaysText = "Today";
        } else if (diffDays === 1) {
            diffDaysText = "Tomorrow";
        } else if (diffDays > 1) {
            diffDaysText = `In ${diffDays} days`;
        } else if (diffDays === -1) {
            diffDaysText = "Yesterday";
        } else {
            diffDaysText = `${Math.abs(diffDays)} days ago`;
        }
    }

    useEffect(() => {
        setIsOverdue(diffDays < 0 && workOrder.status === 'scheduled');
    }, [diffDays, workOrder.status]);

    return (
        <div className="group">
            <div
                className={`w-full rounded-lg border text-white flex flex-col overflow-hidden transition-all duration-200 hover:-translate-y-px ${isCritical
                    ? 'border-rose-700/60 bg-rose-950/25 shadow-sm shadow-rose-950/50 hover:border-rose-600/70 hover:shadow-rose-900/40'
                    : 'border-gray-700/80 bg-gray-900/60 shadow-sm hover:border-gray-500/70 hover:shadow-md hover:shadow-black/30'
                    }`}
            >

                {/* Priority stripe + header row */}
                <div className="flex items-stretch">
                    {/* Left priority stripe */}
                    <div className={`${pColors.stripeW} shrink-0 ${pColors.stripe}`} />

                    <div className="flex-1 min-w-0">
                        {/* Top meta row: priority pill + status badge */}
                        <div className="flex items-center justify-between gap-2 px-2 pt-2 pb-1">
                            <span className={`text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded flex items-center gap-1 ${pColors.badge}`}>
                                {isCritical && <TriangleAlert className="w-3 h-3 shrink-0" />}
                                {priority.charAt(0).toUpperCase() + priority.slice(1)}
                            </span>
                            <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${statusColorMap[workOrder.status] || 'bg-gray-700/50 text-gray-400'}`}>
                                {workOrder.status?.charAt(0).toUpperCase() + workOrder.status?.slice(1).replaceAll('_', ' ')}
                            </span>
                        </div>

                        {/* Title */}
                        <h2 className="font-semibold text-sm text-white px-2 pb-1 leading-snug line-clamp-2">
                            {workOrder.title}
                        </h2>

                        {/* Description */}
                        {workOrder.description && (
                            <p className="px-2 pb-2 text-xs text-gray-400 line-clamp-3 leading-relaxed border-b border-gray-700/60">
                                {workOrder.description}
                            </p>
                        )}

                        {/* Progress bar — only when not simply scheduled */}
                        {workOrder.status !== 'scheduled' && (
                            <div className="relative h-[3px] w-full bg-gray-700/60">
                                <div
                                    className={`absolute top-0 left-0 h-full transition-all duration-300 ${pColors.bar}`}
                                    style={{ width: `${wo_progress}%` }}
                                />
                            </div>
                        )}
                        {workOrder.status !== 'scheduled' && (
                            <div className="px-2 pt-1 pb-1 flex justify-end">
                                <span className="text-[10px] text-gray-500 font-medium">{wo_progress}% complete</span>
                            </div>
                        )}

                        {/* Footer: date + assignee */}
                        <div className="flex items-center justify-between gap-2 px-2 py-1.5 border-t border-gray-700/60">
                            {/* Date */}
                            <div className={`flex items-center gap-1.5 text-xs ${isOverdue ? 'text-red-400' : 'text-gray-400'}`}>
                                {isOverdue
                                    ? <TriangleAlert className="w-3.5 h-3.5 shrink-0" />
                                    : <CalendarDays className="w-3.5 h-3.5 shrink-0 text-gray-500" />
                                }
                                <span className={isOverdue ? 'font-medium' : ''}>
                                    {workOrder.scheduled_date
                                        ? new Date(workOrder.scheduled_date).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
                                        : 'N/A'}
                                </span>
                                {diffDaysText && (
                                    <span className={`ml-1 ${isOverdue ? 'text-red-400 font-semibold' : 'text-gray-500'}`}>· {diffDaysText}</span>
                                )}
                            </div>

                            {/* Assignee */}
                            {workOrder.assignments?.length > 0 && (
                                <div className="flex items-center gap-1 text-xs text-gray-500 min-w-0">
                                    <Users className="w-3.5 h-3.5 shrink-0" />
                                    <span className="truncate max-w-[100px]">
                                        {workOrder.assignments[0]?.employee_name || 'N/A'}
                                    </span>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};